import { Trans } from "@lingui/react/macro";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import React from "react";

import { PermissionGuard } from "@/common/acl/guard/guard";
import { Spinner } from "@/common/components/spinner/spinner";
import { toaster } from "@/common/components/toast/toast";
import { currencyFormatter, getPrice } from "@/common/services/formatter";
import { getLocalizedPeriods } from "@/common/utils/period";

import {
  companyQuery,
  useDeleteCustomPriceMutation,
  useSetCustomPriceMutation,
} from "../company.api";
import { useCompanyId } from "../use-company-id";

const Pricing = () => {
  const id = useCompanyId();
  const companyId = parseInt(id);
  const queryClient = useQueryClient();
  const {
    data: { plans },
  } = useSuspenseQuery(companyQuery({ id: companyId }));

  const setCustomPrice = useSetCustomPriceMutation();
  const deleteCustomPrice = useDeleteCustomPriceMutation();

  const [editingPeriodId, setEditingPeriodId] = React.useState<number | null>(
    null,
  );
  const [editPrice, setEditPrice] = React.useState("");

  const localizedPeriods = getLocalizedPeriods();

  const handleSave = (periodId: number, precision: number) => {
    const displayPrice = parseFloat(editPrice);
    if (isNaN(displayPrice) || displayPrice <= 0) {
      toaster.error(
        { title: <Trans>Zadejte kladnou cenu</Trans> },
        { autoClose: 2000 },
      );
      return;
    }

    const convertedPrice = Math.round(displayPrice * Math.pow(10, precision));
    setCustomPrice.mutate(
      {
        companyId,
        request: { planPeriodId: periodId, price: convertedPrice },
      },
      {
        onError: () => {
          toaster.error(
            { title: <Trans>Nepodařilo se uložit vlastní cenu</Trans> },
            { autoClose: 2000 },
          );
        },
        onSuccess: () => {
          setEditingPeriodId(null);
          void queryClient.invalidateQueries({
            queryKey: ["company", companyId],
          });
        },
      },
    );
  };

  const handleDelete = (periodId: number) => {
    deleteCustomPrice.mutate(
      { companyId, planPeriodId: periodId },
      {
        onError: () => {
          toaster.error(
            { title: <Trans>Nepodařilo se smazat vlastní cenu</Trans> },
            { autoClose: 2000 },
          );
        },
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: ["company", companyId],
          });
        },
      },
    );
  };

  return (
    <details>
      <summary>
        <h2 className="inline text-lg font-bold">
          <Trans>Ceník</Trans>
        </h2>
      </summary>

      <table className="mt-4 w-full text-left text-sm">
        <thead>
          <tr className="border-b text-gray-500">
            <th className="px-2 py-2">
              <Trans>Balíček</Trans>
            </th>
            <th className="px-2 py-2">
              <Trans>Období</Trans>
            </th>
            <th className="px-2 py-2 text-right">
              <Trans>Cena</Trans>
            </th>
            <th className="px-2 py-2">
              <Trans>Typ</Trans>
            </th>
            <PermissionGuard permission="users:company:plan">
              <th className="px-2 py-2">
                <Trans>Akce</Trans>
              </th>
            </PermissionGuard>
          </tr>
        </thead>
        <tbody>
          {plans
            .filter((plan) => plan.periods.length)
            .flatMap((plan) =>
              plan.periods.map((period) => {
                const isEditing = editingPeriodId === period.id;
                const formatter = currencyFormatter(period.currency.type);
                const displayPrice = getPrice(
                  period.price,
                  period.currency.precision,
                );

                return (
                  <tr className="border-b" key={`${plan.id}-${period.id}`}>
                    <td className="px-2 py-2">{plan.name}</td>
                    <td className="px-2 py-2">
                      {localizedPeriods[period.type]}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {isEditing ? (
                        <input
                          autoFocus
                          className="ml-auto w-24 rounded border px-1 py-0.5 text-right"
                          onChange={(e) => setEditPrice(e.target.value)}
                          step="0.01"
                          type="number"
                          value={editPrice}
                        />
                      ) : (
                        formatter.format(displayPrice)
                      )}
                    </td>
                    <td className="px-2 py-2">
                      {period.customPrice ? (
                        <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                          <Trans>Vlastní</Trans>
                        </span>
                      ) : (
                        <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                          <Trans>Systémová</Trans>
                        </span>
                      )}
                    </td>
                    <PermissionGuard permission="users:company:plan">
                      <td className="px-2 py-2">
                        {(setCustomPrice.isPending &&
                          editingPeriodId === period.id) ||
                        (deleteCustomPrice.isPending &&
                          deleteCustomPrice.variables.planPeriodId ===
                            period.id) ? (
                          <Spinner className="h-4! w-4!" withHint={false} />
                        ) : (
                          <div className="flex gap-2 text-can-forest-teal">
                            {isEditing ? (
                              <>
                                <button
                                  onClick={() =>
                                    handleSave(
                                      period.id,
                                      period.currency.precision,
                                    )
                                  }
                                  type="button"
                                >
                                  <Trans>Uložit</Trans>
                                </button>
                                <button
                                  onClick={() => setEditingPeriodId(null)}
                                  type="button"
                                >
                                  <Trans>Zrušit</Trans>
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => {
                                    setEditingPeriodId(period.id);
                                    setEditPrice(displayPrice.toString());
                                  }}
                                  type="button"
                                >
                                  <Trans>Upravit</Trans>
                                </button>
                                {period.customPrice ? (
                                  <button
                                    onClick={() => handleDelete(period.id)}
                                    type="button"
                                  >
                                    <Trans>Smazat</Trans>
                                  </button>
                                ) : null}
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </PermissionGuard>
                  </tr>
                );
              }),
            )}
        </tbody>
      </table>
    </details>
  );
};

export { Pricing };
